import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IGetPolicesOptionsResponse,
  IPoliciesOptions,
  IPresetsOptions,
} from "../../api/service/index.types";
import { IAllowedFeature } from "../../pages/Agent/constants";

export type ListOptions = Record<
  IPresetsOptions["name"],
  Record<IPoliciesOptions["name"], IAllowedFeature>
>;

export interface IPolicesState {
  data: {
    policiesOptions: IGetPolicesOptionsResponse["data"]["policiesOptions"];
    presetsOptions: IGetPolicesOptionsResponse["data"]["presetsOptions"];
    optionsByCategory: ListOptions;
  };
  loading: boolean;
}

export const initialState: IPolicesState = {
  data: {
    policiesOptions: [],
    presetsOptions: [],
    optionsByCategory: {},
  },
  loading: true,
};

const policesSlice = createSlice({
  name: "polices",
  initialState,
  reducers: {
    fetchPolicesRequest: (state) => {
      state.loading = true;
    },
    fetchPolicesSuccess: (state, action: PayloadAction<IGetPolicesOptionsResponse["data"]>) => {
      const listOptions: ListOptions = {};
      action.payload.policiesOptions.forEach(
        ({ id, name, category, parent_name, parent_id, option_name, tool_tip }) => {
          const isSubOption = !!parent_id;
          const isParentOption = !!parent_name;

          const option: Record<IPoliciesOptions["name"], IAllowedFeature> = {
            [name]: {
              name: `allowed_features.${name}`,
              label: option_name,
              isSubOption,
              ...(tool_tip && { contextualHelpText: tool_tip }),
              ...(isParentOption && {
                group: parent_name,
                subOptionsList: [
                  ...(isParentOption
                    ? [
                        ...action.payload.policiesOptions
                          .filter(({ parent_id }) => id === parent_id)
                          .map(({ name }) => name),
                      ]
                    : []),
                ],
              }),
            },
          };

          if (!listOptions[category]) {
            listOptions[category] = {
              ...listOptions[category as keyof ListOptions],
              ...option,
            };
          } else {
            listOptions[category] = {
              ...listOptions[category],
              ...option,
            };
          }
        }
      );

      state.loading = false;
      state.data.policiesOptions = action.payload.policiesOptions;
      state.data.presetsOptions = action.payload.presetsOptions;
      state.data.optionsByCategory = listOptions;
    },
    fetchPolicesFailed: (state) => {
      state.loading = false;
    },
  },
});

export const policesActions = policesSlice.actions;
export default policesSlice.reducer;
